// US-071 — Thread header (contact identity + account chip + AI mode badge +
// hot-override toggle).
//
// Extracted verbatim from `ThreadPanel`'s `<header>` block as part of the
// audit-cleanup structural split. Pure presentational: the parent owns the
// hot-override mutation and passes a resolved toggle handler + pending flag.

import { ExternalLink } from 'lucide-react';

import { useT } from '@barel/i18n';
import { Avatar, IconButton, Pill } from '@barel/ui';

import type { AiMode, ConversationView } from './types';

export interface ThreadHeaderProps {
  conversation: ConversationView;
  aiMode: AiMode;
  hotOverride: boolean;
  /** True while the hot-override mutation is in flight. */
  togglingHotOverride: boolean;
  /** Fired by the header hot-override button. */
  onToggleHotOverride: () => void;
}

export function ThreadHeader({
  conversation,
  aiMode,
  hotOverride,
  togglingHotOverride,
  onToggleHotOverride,
}: ThreadHeaderProps) {
  const t = useT('inbox');

  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-2_5) var(--space-4)',
        borderBottom: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
        flex: '0 0 auto',
      }}
    >
      <Avatar
        size="lg"
        src={conversation.contactAvatarUrl ?? undefined}
        initials={conversation.contactInitials}
        tone="neutral"
        alt={conversation.contactName}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-0_5)',
          flex: '1 1 auto',
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 'var(--text-md)',
            lineHeight: 'var(--line-md)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {conversation.contactName}
        </span>
        {conversation.contactRole || conversation.contactCompany ? (
          <span
            style={{
              fontSize: 'var(--text-2xs)',
              lineHeight: 'var(--line-xs)',
              color: 'var(--text-tertiary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {[conversation.contactRole, conversation.contactCompany].filter(Boolean).join(' · ')}
          </span>
        ) : null}
      </div>
      {conversation.contactProfileUrl ? (
        <IconButton
          aria-label={t('thread.header.openLinkedin')}
          variant="tertiary"
          size="sm"
          icon={<ExternalLink aria-hidden style={{ width: 14, height: 14 }} />}
          onClick={() =>
            window.open(conversation.contactProfileUrl ?? '', '_blank', 'noopener,noreferrer')
          }
        />
      ) : null}
      <Pill
        tone="neutral"
        variant="outline"
        size="xs"
        icon={
          <Avatar
            size={14}
            initials={conversation.account.initials}
            tone={conversation.account.color ? 'identity' : 'neutral'}
            identityColor={conversation.account.color}
            alt=""
            aria-hidden
          />
        }
      >
        {conversation.account.name}
      </Pill>
      <Pill
        tone={hotOverride ? 'error' : aiMode === 'auto' ? 'success' : 'purple'}
        variant="soft"
        size="xs"
      >
        {hotOverride
          ? '🔥'
          : aiMode === 'auto'
            ? `🤖 ${t('thread.header.modeAuto')}`
            : `✦ ${t('thread.header.modeReview')}`}
      </Pill>
      <button
        type="button"
        onClick={onToggleHotOverride}
        disabled={togglingHotOverride}
        data-ls-action="toggle-hot-override"
        data-active={hotOverride || undefined}
        title={t('header.toggle_hot_override_tooltip')}
        aria-pressed={hotOverride}
        aria-label={t('header.toggle_hot_override_tooltip')}
        className="ls-focus-ring"
        style={{
          padding: '4px 8px',
          fontSize: 'var(--text-2xs)',
          fontWeight: 600,
          borderRadius: 6,
          border: `1px solid ${hotOverride ? 'var(--error-border)' : 'var(--border-strong)'}`,
          background: hotOverride ? 'var(--error-bg)' : 'var(--bg-elevated)',
          color: hotOverride ? 'var(--error)' : 'var(--text-secondary)',
          cursor: togglingHotOverride ? 'progress' : 'pointer',
          fontFamily: 'var(--font-sans)',
        }}
      >
        {t('header.toggle_hot_override')}
      </button>
    </header>
  );
}
